// redux/slices/clientGroupsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";
import { resetStore } from "./resetStore";

/* ===================== HELPERS ===================== */

const axiosRequest = async ({
    url,
    method = "GET",
    data,
    headers = {},
}) => {
    const jwt = await getJwtToken();
    const requestMethod = String(method).toUpperCase();

    const hasBody =
        data !== undefined &&
        data !== null &&
        requestMethod !== "GET" &&
        requestMethod !== "HEAD";

    try {
        const config = {
            url: `${getServerUrl()}${url}`,
            method: requestMethod,
            headers: {
                Accept: "application/json",
                ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
                ...(hasBody ? { "Content-Type": "application/json" } : {}),
                ...headers,
            },
        };

        if (hasBody) {
            config.data = data;
        }

        const response = await axios(config);

        return response.data;
    } catch (error) {
        throw {
            status: error?.response?.status,
            data: error?.response?.data,
            message:
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                error?.message ||
                "Request failed",
        };
    }
};

const getErrorMessage = (error, fallback) =>
    error?.data?.error ||
    error?.data?.message ||
    error?.message ||
    fallback;

const upsertGroup = (state, updated) => {
    if (!updated?.id) return;

    const index = state.groups.findIndex(
        (group) => String(group?.id) === String(updated.id)
    );

    if (index !== -1) {
        state.groups[index] = updated;
    } else {
        state.groups.push(updated);
    }
};

/* ===================== THUNKS ===================== */

export const fetchGroups = createAsyncThunk(
    "clientGroups/fetch",
    async (uid, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error("UID не передан");

            const data = await axiosRequest({
                url: `/client-groups/${uid}`,
            });

            return Array.isArray(data) ? data : [];
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Fetch Groups failed"));
        }
    }
);

export const createGroup = createAsyncThunk(
    "clientGroups/create",
    async ({ uid, name }, { rejectWithValue }) => {
        try {
            if (!uid) throw new Error("UID не передан");
            if (!name) throw new Error("Название группы не передано");

            const data = await axiosRequest({
                url: "/client-groups",
                method: "POST",
                data: { uid, name },
            });

            return data || null;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Create Group failed"));
        }
    }
);

export const deleteGroup = createAsyncThunk(
    "clientGroups/delete",
    async (id, { rejectWithValue }) => {
        try {
            if (!id) throw new Error("ID группы не передан");

            await axiosRequest({
                url: `/client-groups/${id}`,
                method: "DELETE",
            });

            return id;
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, "Delete Group failed"));
        }
    }
);

export const addClientToGroup = createAsyncThunk(
    "clientGroups/addClient",
    async ({ groupId, phone }, { rejectWithValue }) => {
        try {
            if (!groupId) throw new Error("ID группы не передан");
            if (!phone) throw new Error("Телефон клиента не передан");

            const data = await axiosRequest({
                url: `/client-groups/${groupId}/add-client`,
                method: "PATCH",
                data: { phone },
            });

            return data || null;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Add Client To Group failed")
            );
        }
    }
);

export const removeClientFromGroup = createAsyncThunk(
    "clientGroups/removeClient",
    async ({ groupId, phone }, { rejectWithValue }) => {
        try {
            if (!groupId) throw new Error("ID группы не передан");
            if (!phone) throw new Error("Телефон клиента не передан");

            const data = await axiosRequest({
                url: `/client-groups/${groupId}/remove-client`,
                method: "PATCH",
                data: { phone },
            });

            return data || null;
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, "Remove Client From Group failed")
            );
        }
    }
);

/* ===================== STATE ===================== */

const initialState = {
    groups: [],
    status: "idle",
    error: null,
};

/* ===================== SLICE ===================== */

const clientGroupsSlice = createSlice({
    name: "clientGroups",
    initialState,
    reducers: {
        clearClientGroupsError: (state) => {
            state.error = null;
        },
        resetClientGroupsStatus: (state) => {
            state.status = "idle";
            state.error = null;
        },
        clearClientGroups: (state) => {
            state.groups = [];
            state.status = "idle";
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGroups.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchGroups.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.groups = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchGroups.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload || action.error?.message || "Fetch Groups failed";
            })

            .addCase(createGroup.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(createGroup.fulfilled, (state, action) => {
                state.status = "succeeded";
                if (action.payload) state.groups.push(action.payload);
            })
            .addCase(createGroup.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload || action.error?.message || "Create Group failed";
            })

            .addCase(deleteGroup.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(deleteGroup.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.groups = state.groups.filter(
                    (group) => String(group?.id) !== String(action.payload)
                );
            })
            .addCase(deleteGroup.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload || action.error?.message || "Delete Group failed";
            })

            .addCase(addClientToGroup.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(addClientToGroup.fulfilled, (state, action) => {
                state.status = "succeeded";
                upsertGroup(state, action.payload);
            })
            .addCase(addClientToGroup.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Add Client To Group failed";
            })

            .addCase(removeClientFromGroup.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(removeClientFromGroup.fulfilled, (state, action) => {
                state.status = "succeeded";
                upsertGroup(state, action.payload);
            })
            .addCase(removeClientFromGroup.rejected, (state, action) => {
                state.status = "failed";
                state.error =
                    action.payload ||
                    action.error?.message ||
                    "Remove Client From Group failed";
            })

            .addCase(resetStore, () => initialState);
    },
});

/* ===================== ACTIONS ===================== */

export const {
    clearClientGroupsError,
    resetClientGroupsStatus,
    clearClientGroups,
} = clientGroupsSlice.actions;

/* ===================== SELECTORS ===================== */

export const selectClientGroupsState = (state) => state.clientGroups;
export const selectClientGroups = (state) =>
    state.clientGroups?.groups || [];
export const selectClientGroupsStatus = (state) =>
    state.clientGroups?.status || "idle";
export const selectClientGroupsError = (state) =>
    state.clientGroups?.error || null;

export default clientGroupsSlice.reducer;